import { useRef, useState } from "react";
import { Upload, FileDown, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const VIOLET = "#6D5DF6";
const TEMPLATES = {
  expenses: {
    name: "modele_depenses.csv",
    content: "type;date;intitulé;catégorie;fournisseur;montant\npro;2025-01-15;Abonnement Canva;Abonnements logiciels;Canva;12,99\nperso;2025-01-20;Courses;Courses alimentaires;;85,40",
    hint: "Colonnes : type (pro/perso), date (AAAA-MM-JJ ou JJ/MM/AAAA), intitulé, catégorie (facultatif), fournisseur (facultatif), montant.",
    accept: ".csv,.xlsx",
  },
  income: {
    name: "modele_ca_historique.csv",
    content: "mois;montant\n2025-01;2500\n2025-02;1800",
    hint: "Colonnes : mois (AAAA-MM), montant. Pour chaque mois, le montant le plus élevé entre les factures enregistrées et cette saisie est retenu (couvre les factures non saisies, sans double comptage).",
    accept: ".csv,.xlsx",
  },
};

export default function ImportModal({ kind, onClose, onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const cfg = TEMPLATES[kind];
  const dlTemplate = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + cfg.content], { type: "text/csv" }));
    a.download = cfg.name;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/pilotage/import/${kind}`, fd);
      setResult(r.data);
      if (r.data.imported > 0) { toast.success(`${r.data.imported} ligne(s) importée(s)`); onDone(); }
      else toast.error("Aucune ligne importée — vérifiez le format");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid={`import-modal-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">
            {kind === "expenses" ? "Importer des dépenses historiques" : "Importer le CA historique"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-neutral-500">{cfg.hint}</p>
        <button onClick={dlTemplate} className="flex items-center gap-1.5 text-[11px] font-medium hover:underline w-fit" style={{ color: VIOLET }} data-testid="import-template">
          <FileDown className="w-3.5 h-3.5" /> Télécharger le modèle CSV
        </button>
        <label className="flex items-center justify-center gap-2 border border-dashed border-neutral-300 rounded-xl px-4 py-6 cursor-pointer hover:bg-neutral-50 text-xs text-neutral-500" data-testid="import-dropzone">
          <input ref={fileRef} type="file" accept={cfg.accept} className="hidden" data-testid="import-file" />
          <Upload className="w-4 h-4" style={{ color: VIOLET }} /> Choisir un fichier CSV ou Excel (.xlsx)
        </label>
        {result && (
          <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-3 text-[11px] space-y-1" data-testid="import-result">
            <p className="font-medium text-neutral-700">{result.imported} importée(s) · {result.skipped} ignorée(s)</p>
            {result.errors?.map((e, i) => <p key={i} className="text-rose-500 flex items-start gap-1"><X className="w-3 h-3 mt-0.5 shrink-0" />{e}</p>)}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="h-8 text-xs" data-testid="import-cancel">Fermer</Button>
          <Button onClick={submit} disabled={busy} className="h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="import-submit">{busy ? "Import en cours…" : "Importer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
